import { useId } from 'react';
import {
  changeShapeType,
  partAt,
  renameMaterial,
  resolvePartPath,
  setMaterial,
  setOps,
  setPartField,
  SHAPE_DEFAULTS,
  TEXTURE_DEFAULTS,
  TEXTURE_PATTERNS,
  type ModelArray,
  type ModelDoc,
  type ModelMaterial,
} from '../../lib/model-doc.js';
import { fullTransform } from '../../lib/scene-doc.js';
import { promptText } from '../../lib/prompt-dialog.js';
import { useModel } from '../../state/model.js';
import { ShapeKeysPanel } from './ShapeKeysPanel.js';
import { newPixelArt } from '../pixel/PixelTab.js';
import { usePixel } from '../../state/pixel.js';
import { useProjectFiles } from '../../state/project-files.js';
import { presetLabel, presetMaterialFields, usePresets } from './TexturePresets.js';
import { SHAPE_EDITORS, ShapeEditor } from './shapes/registry.js';
import { RemeshPanel } from './RemeshPanel.js';
import { BakeCard } from './paint/BakePanel.js';
import { OpStack } from './OpStack.js';
import { createPresetMaterial, MATERIAL_PRESETS } from './material-presets.js';
import { effectiveSelection, renamePart } from '../../lib/model-tree.js';
import { useWorkspace } from '../../state/workspace.js';
import {
  Card,
  ColorField,
  EmptyState,
  IconButton,
  JsonField,
  MenuButton,
  NumberField,
  PropRow,
  SelectField,
  TextField,
  Toggle,
  VecField,
} from '../ui/controls.js';
import { Icon } from '../ui/icons.js';

const AXES = ['x', 'y', 'z'];
const ENUMS: Record<string, readonly string[]> = {
  axis: AXES,
  mode: ['union', 'subtract', 'intersect'],
  method: ['exact', 'sdf'],
  pattern: TEXTURE_PATTERNS,
};

function label(key: string): string {
  return key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());
}

/**
 * Edit an object's fields by value type: numbers, booleans, short numeric
 * vectors and known enums get typed controls; anything else is JSON.
 */
export function ObjectFields({
  value,
  defaults,
  skip = [],
  onChange,
}: {
  value: Record<string, unknown>;
  /** Schema defaults: listed even when absent, shown with their default value. */
  defaults: Record<string, unknown>;
  skip?: string[];
  onChange: (next: Record<string, unknown>) => void;
}) {
  const all = [...new Set([...Object.keys(defaults), ...Object.keys(value)])].filter((k) => !skip.includes(k));
  const set = (key: string, v: unknown) => {
    const next = { ...value };
    if (v === undefined) delete next[key];
    else next[key] = v;
    onChange(next);
  };
  return (
    <>
      {all.map((key) => {
        const v = value[key] ?? defaults[key];
        let control;
        if (ENUMS[key]) {
          control = <SelectField value={typeof v === 'string' ? v : undefined} options={ENUMS[key]!} onChange={(x) => set(key, x)} />;
        } else if (key === 'with') {
          control = <TextField value={typeof v === 'string' ? v : ''} placeholder="part name or path" onChange={(x) => set(key, x)} />;
        } else if (typeof v === 'number') {
          const integer = /segments|rings|sides|levels|iterations|count|seed|octaves|resolution|subdivisions|samples|scratches/.test(key);
          control = <NumberField value={v as number | undefined} integer={integer} step={integer ? 1 : 0.05} onChange={(x) => set(key, x)} />;
        } else if (typeof v === 'boolean') {
          control = <Toggle checked={v} onChange={(x) => set(key, x)} />;
        } else if (Array.isArray(v) && v.length >= 2 && v.length <= 4 && v.every((n) => typeof n === 'number')) {
          control = /color/i.test(key) ? (
            <ColorField value={v as number[]} onChange={(x) => set(key, x)} />
          ) : (
            <VecField value={v as number[]} size={v.length as 2 | 3 | 4} onChange={(x) => set(key, x)} />
          );
        } else {
          control = <JsonField value={v} rows={2} onChange={(x) => set(key, x)} />;
        }
        return (
          <PropRow key={key} label={label(key)} hint={key}>
            {control}
          </PropRow>
        );
      })}
    </>
  );
}

/** Inspector for the selected part of the open model. */
export function PartInspector() {
  const doc = useModel((s) => s.doc);
  const selected = useModel((s) => s.selected);
  const update = useModel((s) => s.update);
  if (!doc) return <EmptyState icon="model" title="No model open" />;
  // Resolved here, not in a store selector: a selector returning a new array
  // on every call makes React re-render forever.
  const index = selected ? resolvePartPath(doc, selected) : null;
  const part = index ? partAt(doc, index) : null;
  if (!part || !index) {
    return (
      <EmptyState icon="layers" title="No part selected">
        Select a part in the tree or the viewport to edit its shape, transform and operations.
      </EmptyState>
    );
  }
  const field = (key: string, value: unknown) => update(setPartField(doc, index, key, value));
  const t = fullTransform(part.transform);
  const shape = part.shape ?? { type: 'empty' };
  const materials = ['default', ...Object.keys(doc.materials ?? {}).filter((m) => m !== 'default')];

  return (
    <div className="inspector-body">
      <div className="inspector-head">
        <span className="inspector-glyph">
          <Icon name="layers" size={16} />
        </span>
        <div className="inspector-titles">
          <TextField
            value={part.name ?? ''}
            placeholder="(unnamed part)"
            onChange={(name) => {
              // Keeps the part selected under its new path; booleans that name it follow.
              const renamed = renamePart(doc, selected ?? '', name);
              if (renamed.doc !== doc) update(renamed.doc, index);
            }}
          />
          <span className="mono muted inspector-id">{selected}</span>
        </div>
        <IconButton
          icon={part.hidden ? 'eyeOff' : 'eye'}
          title={part.hidden ? 'Hidden (still usable as a boolean cutter)' : 'Visible'}
          onClick={() => field('hidden', !part.hidden)}
        />
      </div>

      <Card title="Shape" icon="cube" subtitle={shape.type}>
        <PropRow label="Type">
          <SelectField value={shape.type} options={Object.keys(SHAPE_DEFAULTS)} onChange={(type) => field('shape', changeShapeType(shape, type))} />
        </PropRow>
        {SHAPE_EDITORS[shape.type] ? (
          <ShapeEditor key={selected} shape={shape} doc={doc} partPath={selected ?? ''} onChange={(next) => field('shape', { ...next, type: shape.type })} />
        ) : (
          <ObjectFields
            value={shape}
            defaults={SHAPE_DEFAULTS[shape.type] ?? {}}
            skip={['type']}
            onChange={(next) => field('shape', { ...next, type: shape.type })}
          />
        )}
      </Card>

      <Card title="Transform" icon="move">
        <PropRow label="Position">
          <VecField value={t.position} onChange={(v) => field('transform', { ...part.transform, position: v })} />
        </PropRow>
        <PropRow label="Rotation" hint="degrees">
          <VecField value={t.rotation} step={1} onChange={(v) => field('transform', { ...part.transform, rotation: v })} />
        </PropRow>
        <PropRow label="Scale">
          <VecField value={t.scale} step={0.05} onChange={(v) => field('transform', { ...part.transform, scale: v })} />
        </PropRow>
      </Card>

      <Card title="Material" icon="material" subtitle={part.material ?? 'default'}>
        <PropRow label="Material">
          <SelectField
            value={part.material ?? 'default'}
            options={[...materials, ...MATERIAL_PRESETS.map((p) => ({ value: `+preset:${p.id}`, label: `+ New ${p.label}…` }))]}
            onChange={(m) => {
              const preset = m.startsWith('+preset:') ? MATERIAL_PRESETS.find((p) => `+preset:${p.id}` === m) : undefined;
              if (!preset) {
                field('material', m === 'default' ? undefined : m);
                return;
              }
              const st = useModel.getState();
              const targets = effectiveSelection(st.selected, st.multi);
              void createPresetMaterial(doc, preset, targets.length ? targets : selected ? [selected] : []).then((next) => next && update(next));
            }}
          />
        </PropRow>
      </Card>

      <OpStack ops={part.ops ?? []} partPath={selected!} onChange={(ops) => update(setOps(doc, index, ops))} />
      <ShapeKeysPanel />
      <RemeshPanel />
      <BakeCard path={selected} />

      <Card title="Repeat" icon="prefab" defaultOpen={Boolean(part.array || part.mirror)}>
        <PropRow label="Mirror" hint="copy mirrored across the parent's plane">
          <SelectField
            value={part.mirror ?? 'none'}
            options={['none', 'x', 'y', 'z']}
            onChange={(m) => field('mirror', m === 'none' ? undefined : m)}
          />
        </PropRow>
        <PropRow label="Array">
          <Toggle
            checked={Boolean(part.array)}
            onChange={(on) => field('array', on ? ({ count: 3, offset: [1, 0, 0] } satisfies ModelArray) : undefined)}
          />
        </PropRow>
        {part.array && (
          <ObjectFields
            value={part.array as Record<string, unknown>}
            defaults={{ count: 1, offset: [0, 0, 0], rotation: [0, 0, 0], scale: 1, radial: null }}
            onChange={(next) => field('array', next)}
          />
        )}
        {(part.array || part.mirror) && (
          <PropRow label="Copy UVs" hint="Unique gives each copy its own UV tile, so painting or baking one copy leaves the others alone">
            <SelectField
              value={part.copyUvs ?? 'shared'}
              options={['shared', 'unique']}
              onChange={(v) => field('copyUvs', v === 'unique' ? 'unique' : undefined)}
            />
          </PropRow>
        )}
      </Card>
    </div>
  );
}

/** Image path for a material map: project images, plus New / Edit in the Pixel tab. */
function TextureMapField({ value, suggestedName, onChange }: { value: string; suggestedName: string; onChange: (v: string) => void }) {
  const listId = useId();
  const entries = useProjectFiles((st) => st.entries);
  const images = entries
    .filter((e) => e.kind === 'file' && /\.(png|jpe?g|pixel\.json)$/i.test(e.path))
    .map((e) => e.path);
  return (
    <div className="texture-map-field">
      <TextField mono list={listId} value={value} placeholder="textures/….pixel.json or .png" onChange={onChange} />
      <datalist id={listId}>
        {images.map((p) => (
          <option key={p} value={p} />
        ))}
      </datalist>
      {value.endsWith('.pixel.json') ? (
        <IconButton
          icon="pencil"
          title="Paint this texture in the Pixel tab"
          size={13}
          onClick={() => {
            void usePixel.getState().open(value);
            useWorkspace.getState().setView('pixel');
          }}
        />
      ) : (
        <IconButton
          icon="plus"
          title="New texture (made in the Pixel tab, saved in this project)"
          size={13}
          onClick={async () => {
            const path = await newPixelArt('texture', suggestedName);
            if (!path) return;
            onChange(path);
            useWorkspace.getState().setView('pixel');
          }}
        />
      )}
    </div>
  );
}

/** Toon shader settings with their neutral defaults (`toonSettings`). */
const TOON_DEFAULTS: Record<string, unknown> = {
  threshold: 0,
  softness: 0.07,
  bandThreshold: 0.45,
  bandSoftness: 0.07,
  bandWeight: 0.18,
  shadowColor: [0, 0, 0],
  specularThreshold: 0.55,
  specularSoftness: 0.07,
  specularStrength: 0.45,
  rimThreshold: 0.62,
  rimSoftness: 0.08,
  rimStrength: 0.6,
  ambientFlatten: 0.5,
  metallicDamping: 0.7,
  metallicAmbientDamping: 0.5,
};

/** '' = project default (legacy render style), else the material's shader. */
function shaderOf(m: ModelMaterial): string {
  if (typeof m.shader === 'string' && m.shader) return m.shader;
  if (m.toon === true) return 'toon';
  if (m.toon === false) return 'standard';
  return '';
}

/** Materials of the open model: shader, color, roughness, metallic, emissive, maps. */
export function MaterialsEditor() {
  const doc = useModel((s) => s.doc);
  const update = useModel((s) => s.update);
  const entries = useProjectFiles((s) => s.entries);
  const presets = usePresets();
  if (!doc) return null;
  const presetOptions = Object.entries(presets).flatMap(([p, preset]) => (preset ? [{ value: p, label: presetLabel(p, preset) }] : []));
  const shaderFiles = entries.filter((e) => e.kind === 'file' && e.path.endsWith('.wgsl')).map((e) => e.path);
  const materials = doc.materials ?? {};
  const set = (name: string, m: ModelMaterial | null) => update(setMaterial(doc, name, m));
  const used = usedMaterials(doc);

  return (
    <div className="materials">
      {Object.entries(materials).map(([name, m]) => (
        <Card
          key={name}
          title={name}
          icon="material"
          defaultOpen={false}
          subtitle={used.has(name) ? undefined : 'unused'}
          actions={
            <>
              <span className="swatch" style={{ background: swatch(m.color) }} />
              <MenuButton
                className="ibtn"
                title="Material options"
                items={[
                  {
                    id: 'rename',
                    label: 'Rename…',
                    icon: 'edit',
                    run: async () => {
                      const to = await promptText('Rename material', name);
                      if (to && to !== name) update(renameMaterial(doc, name, to.trim()));
                    },
                  },
                  { id: 'del', label: 'Delete', icon: 'trash', danger: true, disabled: used.has(name), run: () => set(name, null) },
                ]}
              >
                <Icon name="more" size={14} />
              </MenuButton>
            </>
          }
        >
          <PropRow label="Color">
            <ColorField value={m.color} onChange={(v) => set(name, { ...m, color: v })} />
          </PropRow>
          <PropRow label="Roughness">
            <NumberField value={m.roughness ?? 0.8} step={0.05} min={0} max={1} onChange={(v) => set(name, { ...m, roughness: v })} />
          </PropRow>
          <PropRow label="Metallic">
            <NumberField value={m.metallic ?? 0} step={0.05} min={0} max={1} onChange={(v) => set(name, { ...m, metallic: v })} />
          </PropRow>
          <PropRow label="Emissive">
            <ColorField value={m.emissive ?? [0, 0, 0]} alpha={false} onChange={(v) => set(name, { ...m, emissive: v })} />
          </PropRow>
          <PropRow label="Shader" hint="standard (PBR), toon, unlit or a project .wgsl custom shader">
            <SelectField
              value={shaderOf(m)}
              options={[
                { value: '', label: 'Project default' },
                ...[...new Set(['standard', 'toon', 'unlit', ...shaderFiles, shaderOf(m)])].filter(Boolean),
              ]}
              onChange={(v) => {
                const next = { ...m };
                delete next.toon;
                if (v) next.shader = v;
                else delete next.shader;
                set(name, next);
              }}
            />
          </PropRow>
          <PropRow label="Emissive intensity" hint="multiplies emissive; > 1 blooms">
            <NumberField
              value={typeof m.emissiveIntensity === 'number' ? m.emissiveIntensity : 1}
              step={0.1}
              min={0}
              onChange={(v) => {
                const next = { ...m };
                if (v === 1 || v === undefined) delete next.emissiveIntensity;
                else next.emissiveIntensity = v;
                set(name, next);
              }}
            />
          </PropRow>
          <PropRow label="Texture" hint="procedural PBR texture (albedo, normal, roughness)">
            <SelectField
              value={isTexture(m.texture) ? String(m.texture.pattern ?? 'noise') : 'none'}
              options={['none', ...TEXTURE_PATTERNS]}
              onChange={(v) => {
                const next = { ...m };
                if (v === 'none') delete next.texture;
                else next.texture = { ...TEXTURE_DEFAULTS, ...(isTexture(m.texture) ? m.texture : {}), pattern: v };
                set(name, next);
              }}
            />
          </PropRow>
          {presetOptions.length > 0 && (
            <PropRow label="Apply preset" hint="copies a texture preset (textures/presets/*.texpreset.json) into this material">
              <SelectField
                value={undefined}
                placeholder="Choose a preset…"
                options={presetOptions}
                onChange={(p) => {
                  const preset = presets[p];
                  if (preset) set(name, { ...m, ...presetMaterialFields(preset) } as ModelMaterial);
                }}
              />
            </PropRow>
          )}
          {isTexture(m.texture) && (
            <ObjectFields
              value={m.texture}
              defaults={TEXTURE_DEFAULTS}
              skip={['pattern']}
              onChange={(t) => set(name, { ...m, texture: t })}
            />
          )}
          {(
            [
              ['map', 'Image map', 'base color image (uses the mesh UVs)'],
              ['normalMap', 'Normal map', 'tangent-space normal image'],
              ['metallicRoughnessMap', 'Metal/rough map', 'green = roughness, blue = metallic'],
              ['ormMap', 'ORM map', 'red = occlusion, green = roughness, blue = metallic'],
              ['emissiveMap', 'Emissive map', 'glow image, multiplied by Emissive × intensity'],
            ] as const
          ).map(([key, label, hint]) => (
            <PropRow key={key} label={label} hint={hint}>
              <TextureMapField
                value={typeof m[key] === 'string' ? (m[key] as string) : ''}
                suggestedName={`${name}_${key === 'map' ? 'color' : key === 'normalMap' ? 'normal' : key === 'ormMap' ? 'orm' : key === 'emissiveMap' ? 'emission' : 'metal_rough'}`}
                onChange={(v) => {
                  const next = { ...m };
                  if (v.trim()) next[key] = v.trim();
                  else delete next[key];
                  set(name, next);
                }}
              />
            </PropRow>
          ))}
          <PropRow label="Double sided" hint="render back faces (leaves, cloth, cards)">
            <Toggle
              checked={m.doubleSided === true}
              onChange={(v) => {
                const next = { ...m };
                if (v) next.doubleSided = true;
                else delete next.doubleSided;
                set(name, next);
              }}
            />
          </PropRow>
          <PropRow label="UV tiling" hint="uvScale [u, v] for image maps">
            <VecField
              value={Array.isArray(m.uvScale) ? (m.uvScale as number[]) : [1, 1]}
              size={2}
              onChange={(v) => {
                const next = { ...m };
                if (v[0] === 1 && v[1] === 1) delete next.uvScale;
                else next.uvScale = v;
                set(name, next);
              }}
            />
          </PropRow>
          {shaderOf(m) === 'toon' && (
            <ObjectFields
              value={isTexture(m.toonSettings) ? m.toonSettings : {}}
              defaults={TOON_DEFAULTS}
              onChange={(t) => set(name, { ...m, toonSettings: t })}
            />
          )}
          {shaderOf(m).endsWith('.wgsl') &&
            (['properties', 'textures'] as const).map((key) => (
              <PropRow key={key} label={key === 'properties' ? 'Properties' : 'Textures'} hint={key === 'properties' ? 'custom shader @property values by name' : 'custom shader @texture images by name'}>
                <JsonField
                  value={m[key] ?? {}}
                  rows={2}
                  onChange={(v) => {
                    const next = { ...m };
                    if (v && typeof v === 'object' && Object.keys(v).length > 0) next[key] = v;
                    else delete next[key];
                    set(name, next);
                  }}
                />
              </PropRow>
            ))}
        </Card>
      ))}
      <MenuButton
        className="add-component"
        title="New material from a preset (assigned to the selected parts)"
        items={() => [
          { id: 'h', label: 'New material', heading: true },
          ...MATERIAL_PRESETS.map((p) => ({
            id: p.id,
            label: p.label,
            icon: p.icon,
            run: () => {
              const st = useModel.getState();
              const targets = effectiveSelection(st.selected, st.multi);
              void createPresetMaterial(doc, p, targets).then((next) => next && update(next));
            },
          })),
        ]}
      >
        <Icon name="plus" size={14} /> New material ▾
      </MenuButton>
    </div>
  );
}

function isTexture(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function swatch(color: unknown): string {
  const c = Array.isArray(color) ? (color as number[]) : [0.8, 0.8, 0.8];
  return `rgb(${(c[0] ?? 0) * 255} ${(c[1] ?? 0) * 255} ${(c[2] ?? 0) * 255})`;
}

function usedMaterials(doc: ModelDoc): Set<string> {
  const out = new Set<string>();
  const walk = (parts: ModelDoc['parts']) => {
    for (const p of parts) {
      out.add(p.material ?? 'default');
      if (p.children) walk(p.children);
    }
  };
  walk(doc.parts);
  return out;
}
